import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';

function fmt(ts) {
  return ts ? new Date(ts).toLocaleString() : '—';
}

function categoryBadge(c) {
  switch (c) {
    case 'Clock Correction':
      return 'bg-info text-dark';
    case 'Reassignment':
      return 'bg-primary';
    case 'Override':
      return 'bg-warning text-dark';
    case 'Billing':
      return 'bg-success';
    default:
      return 'bg-secondary';
  }
}

export default function AuditLedgerPage() {
  const [logs, setLogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searching, setSearching] = useState(false);
  const [rangeError, setRangeError] = useState('');

  const runSearch = useCallback(async (filters) => {
    const res = await api.searchAuditLogs(filters || {});
    setLogs(res || []);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const [cats] = await Promise.all([api.getAuditCategories(), runSearch()]);
        setCategories(cats || []);
      } catch (err) {
        setLoadError(err.message || 'Failed to load the audit ledger.');
      } finally {
        setLoading(false);
      }
    })();
  }, [runSearch]);

  const apply = async () => {
    // YYYY-MM-DD strings compare chronologically.
    if (from && to && from > to) {
      setRangeError("'From' date must be on or before 'To' date");
      return;
    }
    setRangeError('');
    setLoadError('');
    setSearching(true);
    try {
      await runSearch({ search, category, from, to });
    } catch (err) {
      setLoadError(err.message || 'Search failed.');
    } finally {
      setSearching(false);
    }
  };

  // 8830: clear every filter and reload the unfiltered ledger.
  const reset = async () => {
    setSearch('');
    setCategory('');
    setFrom('');
    setTo('');
    setRangeError('');
    setLoadError('');
    setSearching(true);
    try {
      await runSearch();
    } catch (err) {
      setLoadError(err.message || 'Search failed.');
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="container-fluid py-4" data-testid="audit-ledger-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-fingerprint me-2" aria-hidden="true" />
                Immutable Audit Trails
              </h3>
              <p className="text-muted mb-0 small">
                Read-only, tamper-proof ledger of manual corrections, reassignments, overrides and
                billing changes.
              </p>
            </div>
            <span className="badge bg-dark p-2" data-testid="immutable-badge">
              <i className="fas fa-lock me-1" aria-hidden="true" />
              Immutable — read only
            </span>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="audit-load-error">
          {loadError}
        </div>
      )}

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-md-3">
              <label htmlFor="a-search" className="form-label small fw-bold text-muted">
                Search
              </label>
              <input
                id="a-search"
                className="form-control form-control-sm"
                placeholder="Description, actor, reference, value…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && apply()}
                data-testid="audit-search"
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="a-category" className="form-label small fw-bold text-muted">
                Event Category
              </label>
              <select
                id="a-category"
                className="form-select form-select-sm"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="audit-category"
              >
                <option value="">All categories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label htmlFor="a-from" className="form-label small fw-bold text-muted">
                From
              </label>
              <input
                id="a-from"
                type="date"
                className={`form-control form-control-sm${rangeError ? ' is-invalid' : ''}`}
                value={from}
                max={to || undefined}
                onChange={(e) => {
                  setFrom(e.target.value);
                  setRangeError('');
                }}
                data-testid="audit-from"
              />
            </div>
            <div className="col-md-2">
              <label htmlFor="a-to" className="form-label small fw-bold text-muted">
                To
              </label>
              <input
                id="a-to"
                type="date"
                className={`form-control form-control-sm${rangeError ? ' is-invalid' : ''}`}
                value={to}
                min={from || undefined}
                onChange={(e) => {
                  setTo(e.target.value);
                  setRangeError('');
                }}
                data-testid="audit-to"
              />
              {rangeError && (
                <div className="invalid-feedback" data-testid="audit-range-error">
                  {rangeError}
                </div>
              )}
            </div>
            <div className="col-md-2 d-flex gap-2">
              <button
                className="btn btn-primary btn-sm flex-fill"
                onClick={apply}
                disabled={searching}
                aria-label="Apply filters"
                data-testid="audit-apply"
              >
                {searching ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                ) : (
                  <i className="fas fa-search" aria-hidden="true" />
                )}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm flex-fill"
                onClick={reset}
                disabled={searching}
                data-testid="audit-reset"
              >
                <i className="fas fa-undo me-1" aria-hidden="true" />
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-white pt-4 border-bottom-0 d-flex justify-content-between align-items-center">
          <h5 className="mb-0 text-secondary">
            <i className="fas fa-list me-2" aria-hidden="true" />
            System Audit Records
          </h5>
          <span className="badge bg-secondary p-2" data-testid="audit-count">
            {logs.length} record(s)
          </span>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0" data-testid="audit-table">
                <thead className="bg-light text-secondary small text-uppercase">
                  <tr>
                    <th>Timestamp</th>
                    <th>Actor</th>
                    <th>Category</th>
                    <th>Record Target</th>
                    <th>Description</th>
                    <th>Old Value</th>
                    <th>New Value</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4" data-testid="audit-empty">
                        No audit records match the filter.
                      </td>
                    </tr>
                  )}
                  {logs.map((r) => (
                    <tr key={r.id} data-testid="audit-row" data-category={r.eventCategory} data-ref={r.entityRef || ''}>
                      <td className="small text-nowrap">{fmt(r.occurredAt)}</td>
                      <td className="small">{r.actorEmail || '—'}</td>
                      <td>
                        <span className={`badge ${categoryBadge(r.eventCategory)}`} data-testid="audit-category-badge">
                          {r.eventCategory}
                        </span>
                      </td>
                      <td className="small text-nowrap" data-testid="audit-target">{r.entityRef || '—'}</td>
                      <td className="small" data-testid="audit-description">{r.description}</td>
                      <td className="small text-muted" data-testid="audit-old">{r.oldValue || '—'}</td>
                      <td className="small fw-bold" data-testid="audit-new">{r.newValue || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="card-footer bg-white small text-muted">
          <i className="fas fa-shield-halved me-1" aria-hidden="true" />
          This ledger is append-only and cannot be edited or deleted by any user — enforced at the
          database level.
        </div>
      </div>
    </div>
  );
}
